import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import { Bot, Eye, EyeOff, Lock, User, ArrowRight, AlertCircle } from 'lucide-react'
import './Login.css'

function Login() {
  // Form state - matches Angular: login_form with username/password
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Validation - matches Angular: form_submitted
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()

  // Matches Angular: loginUser({ value, valid })
  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormSubmitted(true)
    setError('')

    // Validation - matches Angular Validators.required
    const valid = username.trim() !== '' && password.trim() !== ''

    console.log({ username, password }, valid)

    if (valid) {
      setLoading(true)
      try {
        const result = await login(username, password)
        if (result.success) {
          navigate('/')
        } else {
          setError(result.error || 'Invalid credentials')
        }
      } catch (err) {
        setError('An error occurred. Please try again.')
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <div className="login-page">
      {/* Background Pattern - matches Angular .login-bg-pattern */}
      <div className="login-bg">
        <div className="login-bg-gradient" />
        <div className="login-bg-pattern" />
      </div>

      {/* Login Card */}
      <motion.div
        className="login-card"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Logo - matches Angular .login-header */}
        <div className="login-header">
          <motion.div
            className="login-logo"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            <div className="login-logo-icon">R</div>
            <div className="login-logo-text">Redstone <span>AI</span></div>
          </motion.div>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        {/* Form - matches Angular #loginForm */}
        <form id="loginForm" onSubmit={handleSubmit} className="login-form">
          {/* General Error */}
          {error && (
            <motion.div
              className="login-error-banner"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={16} />
              {error}
            </motion.div>
          )}

          {/* Username Field - matches Angular */}
          <motion.div
            className="login-form-group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label className="login-label">
              <User size={14} /> Username
            </label>
            <input
              type="text"
              className={`login-input ${formSubmitted && !username.trim() ? 'error' : ''}`}
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            {/* Validation Error - matches Angular *ngIf */}
            {formSubmitted && !username.trim() && (
              <div className="login-error">
                <AlertCircle size={12} /> Enter username
              </div>
            )}
          </motion.div>

          {/* Password Field - matches Angular */}
          <motion.div
            className="login-form-group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <label className="login-label">
              <Lock size={14} /> Password
            </label>
            <div className="login-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className={`login-input ${formSubmitted && !password.trim() ? 'error' : ''}`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/* Validation Error - matches Angular *ngIf */}
            {formSubmitted && !password.trim() && (
              <div className="login-error">
                <AlertCircle size={12} /> Enter your password
              </div>
            )}
          </motion.div>

          {/* Submit Button - matches Angular .login-button */}
          <motion.button
            type="submit"
            className="login-button"
            disabled={loading}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? (
              <div className="loading-spinner" />
            ) : (
              <>
                <ArrowRight size={18} /> Sign In
              </>
            )}
          </motion.button>
        </form>

        {/* Footer - matches Angular .login-footer */}
        <motion.div
          className="login-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <p>© 2024 Redstone AI. All rights reserved.</p>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default Login